import clsx from 'clsx';
import React from 'react';
import Button from '../../components/Button';
import { useButtonSizeSmaller } from '../../utils/buttonSizes';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import DatePicker from '../../components/DatePicker';
import Input from '../../components/Input';
import Checkbox from '../../components/Checkbox';
import { useDispatch } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import SaveAsDraft from '../../components/SaveAsDraft';
import moment from 'moment';
import { LoginResponseData } from '@EHSNavigator/backend';
import { useLocalStorage } from '@uidotdev/usehooks';
import TimePicker from '../../components/TimePicker';

interface IncidentReportingStepOneProps {
  otherClasses?: string;
  formHandler: any;
  isDraftLoading: boolean;
  handleSaveAsDraft: () => void;
  handleSteps: React.Dispatch<number>;
  disbaleButtons: boolean;
}

const IncidentReportingStepOne: React.FC<IncidentReportingStepOneProps> = ({
  otherClasses,
  formHandler,
  handleSteps,
  handleSaveAsDraft,
  isDraftLoading,
  disbaleButtons,
}) => {
  const incidentReportingStepOneClasses = clsx(
    otherClasses,
    'w-full flex flex-col flex-1 gap-8 2xl:gap-16 ',
  );
  const dispatch = useDispatch();
  const sizes = useButtonSizeSmaller();
  const [user] = useLocalStorage<LoginResponseData | null>('userInfo', null);
  const dateOfHire = user?.user?.dateOfHire;
  const momentDate = moment(dateOfHire).isValid()
    ? moment(dateOfHire).toDate()
    : moment('2025-01-01').toDate();

  return (
    <div
      className={incidentReportingStepOneClasses}
      data-testid="incident-reporting-step-one"
    >
      <div className="flex flex-col lg:gap-10 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex max-w-818 flex-1 items-start gap-2.5">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <div>
            <p className="font-inter-variable text-brand-byzantine -tracking-form-label mb-1 text-xs leading-4 font-bold lg:hidden">
              Report an Incident
            </p>
            <h1 className="h3 mb-1 lg:mb-0">When & Where Did it Happen?</h1>
            <p className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
              STEP 1 / 3
            </p>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              If you have encountered an incident, please report it here for
              quick and timely resolution of the issue.
            </p>
          </div>
        </div>
        <SaveAsDraft
          handleSaveAsDraft={handleSaveAsDraft}
          isDisabled={isDraftLoading}
          disbaleButtons={disbaleButtons}
        />
      </div>
      <div className="2xl:border-y-neutral-tones-text/10 grid grid-cols-1 2xl:grid-cols-12 2xl:gap-100 2xl:border-y 2xl:py-25">
        <div className="border-y-neutral-tones-text/10 flex flex-col items-start gap-5 border-y py-25 2xl:col-span-7 2xl:border-y-0 2xl:py-0">
          <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
            Your Information
          </h6>
          <div className="grid w-full grid-cols-2 gap-5 sm:grid-cols-4 lg:flex lg:items-start lg:justify-between lg:gap-0">
            {formHandler?.values?.role !== 'Owner' && (
              <div className="col-span-2 flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text/80 font-inter-variable -tracking-button text-sm leading-4 font-bold lg:text-xl lg:leading-6">
                  Employee ID
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input leadinn-4 text-sm font-normal lg:text-base lg:leading-5">
                  {formHandler?.values?.employeeId}
                </p>
              </div>
            )}
            <div className="flex flex-col gap-2.5">
              <h6 className="text-neutral-tones-text/80 font-inter-variable -tracking-button text-sm leading-4 font-bold lg:text-xl lg:leading-6">
                Name
              </h6>
              <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-sm leading-4 font-normal capitalize lg:text-base lg:leading-5">
                {formHandler?.values?.name}
              </p>
            </div>
            <div className="flex justify-end lg:justify-normal">
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text/80 font-inter-variable -tracking-button text-sm leading-4 font-bold lg:text-xl lg:leading-6">
                  Role
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-sm leading-4 font-normal lg:text-base lg:leading-5">
                  {formHandler?.values?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="border-b-neutral-tones-text/10 flex flex-col items-start gap-5 border-b py-25 2xl:col-span-5 2xl:border-b-0 2xl:py-0">
          <h6 className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
            Other Information
          </h6>
          <div className="flex flex-col gap-2.5">
            <h6 className="text-neutral-tones-text/80 font-inter-variable -tracking-button text-sm leading-4 font-bold lg:text-xl lg:leading-6">
              Date of Report
            </h6>
            <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-sm leading-4 font-normal lg:text-base lg:leading-5">
              {formHandler?.values?.dateOfReport}
            </p>
          </div>
        </div>
      </div>
      <div className="gap- flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-start gap-8 xl:flex-row xl:gap-100">
            <div className="flex w-full flex-col lg:w-auto">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-25 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
                Title
              </h6>
              <Input
                onBlur={formHandler?.handleBlur}
                label="Title of the Incident*"
                errorMessage={
                  formHandler?.touched.title && formHandler?.errors.title
                    ? formHandler?.errors.title
                    : ''
                }
                disabled={formHandler?.isSubmitting}
                value={formHandler?.values.title}
                type="text"
                placeholder="Title of Incident (3-6 words)"
                onChange={formHandler?.handleChange}
                name="title"
                otherClasses={{ container: 'w-full lg:w-96 2xl:w-465' }}
              />
            </div>
            <div className="flex w-full flex-1 flex-col xl:w-auto">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-25 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
                Date & Time
              </h6>
              <div className="flex w-full flex-col items-start gap-8 lg:flex-row lg:gap-8">
                <DatePicker
                  otherClasses={{ container: 'w-full lg:max-w-235' }}
                  label="Date*"
                  name="date"
                  value={formHandler?.values?.date}
                  startMonth={momentDate}
                  endMonth={moment().toDate()}
                  placeholder="Select Date"
                  // onBlur={formHandler.handleBlur}
                  onBlur={() => {
                    if (formHandler?.values?.date) {
                      formHandler?.setFieldTouched('date', true);
                    }
                  }}
                  disabled={
                    formHandler.isSubmitting ||
                    formHandler?.values?.dateTimeUndetermined
                  }
                  onChange={(e) => formHandler?.setFieldValue('date', e, true)}
                  errorMessage={
                    formHandler?.touched.date && formHandler?.errors.date
                      ? formHandler?.errors.date
                      : ''
                  }
                />

                <TimePicker
                  otherClasses={{ container: 'w-full lg:max-w-235' }}
                  label="Time*"
                  name="time"
                  value={formHandler.values.time}
                  placeholder="Select Time"
                  onChange={(e) => {
                    formHandler?.handleChange(e);
                  }}
                  onBlur={formHandler?.handleBlur}
                  errorMessage={
                    formHandler?.touched.time && formHandler?.errors.time
                      ? formHandler?.errors.time
                      : ''
                  }
                  disabled={
                    formHandler.isSubmitting ||
                    formHandler?.values?.dateTimeUndetermined
                  }
                />
              </div>
            </div>
          </div>

          <Checkbox
            checked={formHandler?.values?.dateTimeUndetermined}
            name="dateTimeUndetermined"
            otherClasses="w-fit mt-4"
            onChange={() => {
              const newDateTimeUndetermined =
                !formHandler?.values?.dateTimeUndetermined;
              formHandler?.setFieldValue(
                'dateTimeUndetermined',
                newDateTimeUndetermined,
                true,
              );
            }}
            disabled={false}
            errorMessage=""
            onBlur={formHandler.handleBlur}
            label="Date/Time Undetermined"
            aria-label="Date/Time Undetermined"
            aria-describedby="date-time-undetermined-error"
          />
        </div>
        <div className="flex flex-col">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-25 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Location of Incident
          </h6>
          <Input
            onBlur={formHandler?.handleBlur}
            label="Specify Location*"
            errorMessage={
              formHandler?.touched.factory && formHandler?.errors.factory
                ? formHandler?.errors.factory
                : ''
            }
            disabled={formHandler?.isSubmitting}
            value={formHandler?.values.factory}
            type="text"
            placeholder="Enter the location "
            onChange={formHandler?.handleChange}
            name="factory"
            otherClasses={{ container: 'w-full lg:w-465' }}
          />
        </div>
      </div>
      <div className="flex items-center gap-8">
        <Button
          onClick={() => handleSteps(2)}
          size={sizes}
          otherClasses="max-w-211 w-full"
          disable={disbaleButtons}
        >
          Proceed{' '}
          <ChevronIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
        </Button>
      </div>
    </div>
  );
};

export default IncidentReportingStepOne;
